export const vertSrc = `#version 300 es
in vec2 aPos;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`

export const fragSrc = `#version 300 es
precision highp float;

uniform vec2  uRes;
uniform float uPhase;
uniform float uDrift;
uniform float uScale;
uniform float uBlur;
uniform float uWarp;
uniform float uSpread;
uniform float uSoft;
uniform float uLateral;
uniform float uGrain;
uniform float uGrainSize;
uniform float uPixelScale;
uniform float uSeed;
uniform float uBand1;
uniform float uBand2;
uniform float uBand3;
uniform float uBand4;
uniform float uBand5;
uniform vec3  uHaze;
uniform vec3  uWash;
uniform vec3  uMist;
uniform vec3  uInk;
uniform vec3  uOlive;
uniform vec3  uAcid;

out vec4 fragColor;

const float TAU = 6.2831853;

vec2 hash2(vec2 p){
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}

float gnoise(vec2 p){
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(dot(hash2(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
                 dot(hash2(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
             mix(dot(hash2(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                 dot(hash2(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
}

float fbm(vec2 p){
  float detail = 5.0 - uBlur;
  float amp = 0.5;
  float sum = 0.0;
  mat2 rot = mat2(0.80, 0.60, -0.60, 0.80);
  for(int i = 0; i < 5; i++){
    float w = clamp(detail - float(i), 0.0, 1.0);
    sum += amp * w * gnoise(p);
    p = rot * p * 2.02;
    amp *= 0.5;
  }
  return sum;
}

float rand(vec2 co){
  return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
}

float hash21(vec2 p){
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float triNoise(vec2 p){
  return hash21(p) + hash21(p + 19.19) - 1.0;
}

vec3 filmGrain(vec2 fragCoord, float lum){
  float px = max(uGrainSize, 0.35) * uPixelScale;
  vec2 gp = floor(fragCoord / px);
  float s = uSeed * 47.0;

  float fine = triNoise(gp + s);
  float med  = triNoise(floor(gp * 0.5) + s + 31.0) * 0.85;
  float dens = gnoise((fragCoord / uPixelScale) * (0.045 / max(uGrainSize, 0.5)) + s * 0.01);

  float g = fine * 0.62 + med * 0.38;
  g *= 0.78 + 0.44 * dens;

  float mask = 1.0 - abs(lum * 2.0 - 1.0);
  mask = mix(0.40, 1.0, mask * mask);

  float cr = triNoise(gp + s + 3.7);
  float cb = triNoise(gp + s + 8.1);
  vec3 chroma = vec3(1.0 + cr * 0.14, 1.0 - (cr + cb) * 0.04, 1.0 + cb * 0.12);

  return vec3(g) * chroma * uGrain * mask;
}

void main(){
  vec2 uv = gl_FragCoord.xy / uRes;
  vec2 p  = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;

  float xScale = mix(1.0, 0.18, clamp(uLateral, 0.0, 1.0));
  p.x *= xScale;

  p = p * uScale + uSeed;

  vec2 driftOffset = vec2(cos(uPhase * TAU), sin(uPhase * TAU)) * uDrift;
  driftOffset.x *= mix(1.0, 1.7, uLateral);
  driftOffset.y *= mix(1.0, 0.45, uLateral);

  vec2 q = vec2(fbm(p + vec2(0.0, 0.0) + driftOffset),
                fbm(p + vec2(5.2, 1.3) - driftOffset));
  vec2 r = vec2(fbm(p + uWarp * q + vec2(1.7, 9.2)),
                fbm(p + uWarp * q + vec2(8.3, 2.8)));
  float n = fbm(p + uWarp * r);

  float ramp = uv.y * 1.15 - 0.06 + n * uSpread;

  float e = 0.01 + uSoft * 0.32;

  vec3 col = uAcid;
  col = mix(col, uOlive, smoothstep(uBand5 - e, uBand5 + e, ramp));
  col = mix(col, uInk,   smoothstep(uBand4 - e, uBand4 + e, ramp));
  col = mix(col, uMist,  smoothstep(uBand3 - e, uBand3 + e, ramp));
  col = mix(col, uWash,  smoothstep(uBand2 - e, uBand2 + e, ramp));
  col = mix(col, uHaze,  smoothstep(uBand1 - e, uBand1 + e, ramp));

  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col += filmGrain(gl_FragCoord.xy, lum);

  col += (rand(gl_FragCoord.xy * 1.7 + 13.0) - 0.5) / 255.0;

  fragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`
